import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import mixpanel from 'mixpanel-browser';
import { SET_CHECKLIST_TASK } from '../../../queries';
import { PetParent } from '../../../types/PetParent';
import { ChecklistData, ChecklistTaskData } from './checklistData';
import { CheckedMap, computeStats, StageStats } from './checklistUtils';

interface UseChecklistResult {
  checked: CheckedMap;
  expanded: CheckedMap;
  stats: StageStats[];
  done: number;
  total: number;
  percent: number;
  nextTask: ChecklistTaskData | null;
  onCheck(taskId: string): void;
  onExpand(taskId: string): void;
  goToTask(): void;
  registerTaskRef(taskId: string, el: HTMLDivElement | null): void;
  toast: string | null;
  clearToast(): void;
}

/**
 * Checklist state shared by the adopter and foster Home: server-persisted completion
 * with an optimistic overlay, expanded rows, `?task=` return handling and Go To Task.
 */
export default function useChecklist(
  checklist: ChecklistData,
  { petParent, loading }: { petParent: PetParent | null; loading: boolean },
): UseChecklistResult {
  // Server-persisted completion state, overlaid with this session's optimistic
  // toggles so checking feels instant while the mutation is in flight.
  const serverChecked = React.useMemo(() => {
    const map: CheckedMap = {};
    (petParent?.checklistCompletedTasks || []).forEach((key: string) => { map[key] = true; });
    return map;
  }, [petParent?.checklistCompletedTasks]);
  const [optimistic, setOptimistic] = React.useState<CheckedMap>({});
  const checked = React.useMemo(
    () => ({ ...serverChecked, ...optimistic }),
    [serverChecked, optimistic],
  );

  const [expanded, setExpanded] = React.useState<CheckedMap>({});
  const expandedInitialized = React.useRef(false);

  const [toast, setToast] = React.useState<string | null>(null);
  const [setChecklistTask] = useMutation(SET_CHECKLIST_TASK);

  const {
    stats, done, total, percent,
  } = computeStats(checklist, checked);
  const flatTasks = React.useMemo(() => checklist.stages.flatMap((s) => s.tasks), [checklist]);
  const nextTask = flatTasks.find((t) => !checked[t.id]) || null;

  // A lesson opened from a checklist card sends the parent back to
  // /hub/home?task=<id>, so returning reopens the section they were reading rather
  // than resetting to the first unchecked task (QA 3.8).
  const [params, setParams] = useSearchParams();
  const requestedTask = params.get('task');

  // Open the requested task, or the first unchecked one, on first render with data.
  React.useEffect(() => {
    if (expandedInitialized.current || loading || !petParent) return;
    expandedInitialized.current = true;
    const target = (requestedTask && flatTasks.some((t) => t.id === requestedTask))
      ? requestedTask
      : nextTask?.id;
    if (target) setExpanded({ [target]: true });
  }, [loading, petParent, nextTask, requestedTask, flatTasks]);

  const taskRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const registerTaskRef = (taskId: string, el: HTMLDivElement | null) => {
    if (el) taskRefs.current[taskId] = el;
  };

  // Scroll the requested task into view, then drop the param so a later reload or
  // share of this URL doesn't keep yanking the page down to it.
  //
  // Two things fight this and both need waiting out: ScrollToTop resets to 0 on the
  // same navigation, and the task row's ref only registers once the checklist has
  // rendered. Rather than assume an ordering, poll briefly until the row exists.
  //
  // Deliberately setTimeout and not requestAnimationFrame: rAF does not fire at all
  // while the page is hidden, which would leave a parent returning to a background
  // tab stranded at the top of the page.
  const scrolledToTask = React.useRef(false);
  React.useEffect(() => {
    if (scrolledToTask.current || loading || !requestedTask) return undefined;

    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const tryScroll = () => {
      const el = taskRefs.current[requestedTask];
      if (!el) {
        attempts += 1;
        if (attempts > 40) return;
        timer = setTimeout(tryScroll, 25);
        return;
      }
      scrolledToTask.current = true;
      el.scrollIntoView({ block: 'center' });
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('task');
        return next;
      }, { replace: true });
    };
    timer = setTimeout(tryScroll, 0);
    return () => clearTimeout(timer);
  }, [loading, requestedTask, setParams]);

  const clearOptimistic = (taskId: string) => {
    setOptimistic((s) => {
      const next = { ...s };
      delete next[taskId];
      return next;
    });
  };

  const onCheck = (taskId: string) => {
    const completed = !checked[taskId];
    setOptimistic((s) => ({ ...s, [taskId]: completed }));
    const taskName = flatTasks.find((t) => t.id === taskId)?.title;
    mixpanel.track('checklist_task_check', { task_name: taskName, task_key: taskId, completed });
    setChecklistTask({
      variables: { taskKey: taskId, completed },
      // The mutation returns the full completed-task list; make it the cached
      // server state so the optimistic overlay can be dropped once settled.
      update: (cache, { data }) => {
        const list = data?.setChecklistTask;
        if (list && petParent?.id) {
          cache.modify({
            id: cache.identify({ __typename: 'PetParentSingular', id: petParent.id }),
            fields: { checklistCompletedTasks: () => list },
          });
        }
      },
    })
      .then(() => clearOptimistic(taskId))
      .catch(() => {
        // Revert to server truth so the UI never silently shows unsaved state.
        clearOptimistic(taskId);
        setToast("We couldn't save that change — please try again.");
      });
  };

  const onExpand = (taskId: string) => {
    setExpanded((s) => ({ ...s, [taskId]: !s[taskId] }));
  };

  const goToTask = () => {
    if (!nextTask) return;
    if (!expanded[nextTask.id]) onExpand(nextTask.id);
    requestAnimationFrame(() => {
      const el = taskRefs.current[nextTask.id];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.animate(
          [{ background: 'rgba(98, 0, 238, 0.12)' }, { background: 'rgba(98, 0, 238, 0)' }],
          { duration: 1200, easing: 'ease-out' },
        );
      }
    });
  };

  return {
    checked,
    expanded,
    stats,
    done,
    total,
    percent,
    nextTask,
    onCheck,
    onExpand,
    goToTask,
    registerTaskRef,
    toast,
    clearToast: () => setToast(null),
  };
}
